import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type EnrichmentStatusType =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export interface ProspectEnrichmentStatus {
  status: EnrichmentStatusType;
  provider?: string;
  providerIndex?: number;
  totalProviders?: number;
  result?: {
    email?: string;
    phone?: string;
    provider?: string;
  };
  error?: string;
}

interface EnrichmentStatusState {
  prospects: Record<string, ProspectEnrichmentStatus>;
}

const initialState: EnrichmentStatusState = {
  prospects: {},
};

const enrichmentStatusSlice = createSlice({
  name: 'enrichmentStatus',
  initialState,
  reducers: {
    setProspectStatus: (
      state,
      action: PayloadAction<{
        prospectId: string;
        status: ProspectEnrichmentStatus;
      }>
    ) => {
      state.prospects[action.payload.prospectId] = action.payload.status;
    },

    setMultipleProspectStatuses: (
      state,
      action: PayloadAction<
        Array<{ prospectId: string; status: ProspectEnrichmentStatus }>
      >
    ) => {
      action.payload.forEach(({ prospectId, status }) => {
        state.prospects[prospectId] = status;
      });
    },

    updateProspectProgress: (
      state,
      action: PayloadAction<{
        prospectId: string;
        provider?: string;
        providerIndex?: number;
        totalProviders?: number;
      }>
    ) => {
      const existing = state.prospects[action.payload.prospectId];
      if (existing) {
        existing.status = 'processing';
        existing.provider = action.payload.provider;
        existing.providerIndex = action.payload.providerIndex;
        existing.totalProviders = action.payload.totalProviders;
      }
    },

    completeProspectEnrichment: (
      state,
      action: PayloadAction<{
        prospectId: string;
        result?: {
          email?: string;
          phone?: string;
          provider?: string;
        };
      }>
    ) => {
      const existing = state.prospects[action.payload.prospectId];
      if (existing) {
        existing.status = 'completed';
        existing.result = action.payload.result;
        existing.provider = undefined;
        existing.providerIndex = undefined;
        existing.totalProviders = undefined;
      }
    },

    failProspectEnrichment: (
      state,
      action: PayloadAction<{
        prospectId: string;
        error?: string;
      }>
    ) => {
      const existing = state.prospects[action.payload.prospectId];
      if (existing) {
        existing.status = 'failed';
        existing.error = action.payload.error;
        existing.provider = undefined;
        existing.providerIndex = undefined;
        existing.totalProviders = undefined;
      }
    },

    clearProspectStatus: (state, action: PayloadAction<string>) => {
      delete state.prospects[action.payload];
    },

    clearMultipleProspectStatuses: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((prospectId) => {
        delete state.prospects[prospectId];
      });
    },

    clearAllStatuses: (state) => {
      state.prospects = {};
    },
  },
});

export const {
  setProspectStatus,
  setMultipleProspectStatuses,
  updateProspectProgress,
  completeProspectEnrichment,
  failProspectEnrichment,
  clearProspectStatus,
  clearMultipleProspectStatuses,
  clearAllStatuses,
} = enrichmentStatusSlice.actions;

export const enrichmentStatusReducer = enrichmentStatusSlice.reducer;
